import { Home } from "lucide-react";
import { buildPortalTiles } from "../portalTiles.js";
import { TONE_BG } from "@/constants/tones";

/**
 * Portail Client — page d'accueil avec bulles modules.
 * Batch #14.322 — le catalogue et la règle d'affichage vivent dans `portalTiles.js` (partagés avec la barre mobile).
 */

export default function PortalLauncher({ onSelect, client = {}, portalPermissions = null, tiles: tilesProp, ...flags }) {
  const TILES = tilesProp || buildPortalTiles({ ...flags, portalPermissions });
  const greeting = client?.first_name ? `Bonjour ${client.first_name}` : "Bonjour";

  // Phase 7.6 — Branding dynamique : déduit la thématique de l'expertise dominante
  // (basé sur les tuiles cochées). Style Fiscal vs Style Immeuble.
  const isImmeubleDominant = !!portalPermissions?.immeuble && !portalPermissions?.t777;
  const themeAccent = isImmeubleDominant ? "emerald" : "blue";
  const themeBg = isImmeubleDominant
    ? "bg-gradient-to-br from-emerald-50/40 via-white to-white"
    : "bg-gradient-to-br from-blue-50/40 via-white to-white";

  return (
    <div className={`max-w-4xl mx-auto ${themeBg} rounded-2xl p-4 sm:p-6 -m-2 sm:-m-4`} data-testid="portal-launcher" data-portal-theme={themeAccent}>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{greeting}</h1>
        <p className="text-sm text-gray-500 mt-1">Voici vos espaces de travail</p>
      </div>

      {TILES.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="portal-no-tiles">
          <Home className="w-6 h-6 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">
            Votre portail n'est pas encore configuré. Contactez votre conseiller pour activer les services.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5">
          {TILES.map((t) => (
            <button
              key={t.id}
              onClick={() => onSelect(t.id)}
              className={`group relative bg-gradient-to-br ${TONE_BG[t.tone]} rounded-2xl p-5 sm:p-6 flex flex-col items-center justify-center text-center transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg border min-h-[140px]`}
              data-testid={`portal-tile-${t.id}`}
            >
              {t.badge > 0 && (
                <span
                  className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center shadow-sm"
                  data-testid={`portal-badge-${t.id}`}
                >
                  {t.badge > 99 ? "99+" : t.badge}
                </span>
              )}

              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition">
                <t.icon className="w-7 h-7 sm:w-8 sm:h-8" />
              </div>
              <span className="font-semibold text-sm sm:text-base mb-0.5">{t.label}</span>
              <span className="text-[10px] sm:text-xs opacity-75">{t.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
